import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

import EscapeNewLine from "~/components/common/escape-new-line";
import HighlightedText from "~/components/common/highlighted-text";
import Editorials from "~/types/editorials";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import EditorialLink from "../editorial-link/editorial-link";
import styles from "./tabbed-header.module.scss";

type Props = {
  entry: Entry<unknown>;
};

export function HeaderTab(props: { entry: Entry<Editorials.Link>; isCurrent: boolean }) {
  const inspectorModeLink = contentfulUtils.useInspectorMode(props.entry);
  const router = useRouter();
  const { text, url, openOnANewTab } = props.entry.fields;

  return (
    <div {...inspectorModeLink?.getProps("text")}>
      <Link
        href={textUtils.sanitizeContentfulUrl(props.entry, router)}
        target={openOnANewTab ? "_blank" : undefined}
        className={classNames(styles.navLink, props.isCurrent ? styles.selectedNavLink : undefined)}
      >
        {text}
      </Link>
    </div>
  );
}

export default function TabbedHeader(props: Props) {
  assertEditorialType<Editorials.TabbedHeader>(props.entry, "tabbedHeader");

  const { previousPageLink, pretitle, title, date, description, tabs, removePaddingOfBottomElement, backgroundColor } =
    props.entry.fields;

  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);
  const router = useRouter();

  const refTabs = useRef<HTMLLIElement[] | null[] | undefined[]>([]);

  const findTab = tabs
    ?.filter(isActiveEntry)
    .find((tab) => textUtils.sanitizeContentfulUrl(tab, router) === router.asPath);
  const getIndexTab = findTab ? tabs?.filter(isActiveEntry).indexOf(findTab) : undefined;
  const [currentIndex, setCurrentIndex] = useState<number>();

  useEffect(() => {
    refTabs.current[getIndexTab ? getIndexTab : 0]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
    setCurrentIndex(getIndexTab);
  }, [getIndexTab]);

  return (
    <>
      <div
        className={classNames(
          styles.wrapper,
          removePaddingOfBottomElement ? styles.removePaddingOfBottomElement : undefined,
          backgroundColor === "gradient" ? styles.backgroundGradient : styles.backgroundDefault
        )}
      >
        {previousPageLink && <EditorialLink entry={previousPageLink} className={styles.previousLink} />}
        {pretitle && (
          <div {...inspectorMode?.getProps("pretitle")} className={styles.pretitle}>
            <EscapeNewLine text={pretitle} />
          </div>
        )}

        {title && (
          <h1 {...inspectorMode?.getProps("title")} className={styles.title}>
            <HighlightedText text={title} />
          </h1>
        )}
        {date && (
          <div {...inspectorMode?.getProps("date")} className={styles.date}>
            <EscapeNewLine text={date} />
          </div>
        )}
        {description && (
          <div {...inspectorMode?.getProps("description")} className={styles.description}>
            <EscapeNewLine text={description} />
          </div>
        )}
      </div>
      {tabs && (
        <nav
          className={classNames(
            styles.nav,
            removePaddingOfBottomElement ? styles.removePaddingOfBottomElement : undefined
          )}
        >
          <ul className={styles.navList}>
            {tabs.filter(isActiveEntry).map((tab, index) => {
              return (
                <li
                  key={`${tab.sys.id}-${index}`}
                  className={styles.navItem}
                  ref={(ref) => {
                    refTabs.current[index] = ref;
                  }}
                >
                  <HeaderTab entry={tab} isCurrent={index === currentIndex} />
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </>
  );
}
